import { MapDrawingConfig, drawSelectedRect } from "../canvas-drawing";
import {
  Gallery,
  GalleryCell,
  GalleryCellItem,
  Viewport,
} from "PosterEditor/model/poster-project";
import { Point, Rect } from "PosterEditor/model/poster-types";
import { MapElement, DragHandle } from "../canvas-drawing";
import { pointIsInsideRect } from "../point-utils";
import { SnapLine } from "../rect-snapping";
import { rectWithPadding } from "../rect-utils";
import { drawResizeHandle } from "./canvas-resize-handle";

// ============================================================================
// Constants
// ============================================================================

/** Minimum scale value - aspect-fill with no gaps */
export const MIN_CONTENT_SCALE = 1.0;
/** Maximum scale value - prevents sub-pixel source rectangles */
export const MAX_CONTENT_SCALE = 5.0;
/** Minimum offset value - fully panned to left/top edge */
export const MIN_CONTENT_OFFSET = -0.5;
/** Maximum offset value - fully panned to right/bottom edge */
export const MAX_CONTENT_OFFSET = 0.5;

// ============================================================================
// Slack Calculation (for pan bounds)
// ============================================================================

/**
 * Represents the available pan range in each dimension.
 * Slack is how much the image extends beyond the viewport.
 * - slackX > 0 means the image is wider than needed, allowing horizontal panning
 * - slackY > 0 means the image is taller than needed, allowing vertical panning
 */
export interface ContentSlack {
  slackX: number;
  slackY: number;
}

/**
 * Calculates the available pan range (slack) for an image in a viewport.
 *
 * Slack represents how much the image extends beyond the viewport in each dimension.
 * This depends on both the aspect ratio difference AND the current scale.
 *
 * At scale 1.0 (aspect-fill minimum):
 * - If image is wider than viewport (imageAspect > viewportAspect): slackX > 0, slackY = 0
 * - If image is taller than viewport (imageAspect < viewportAspect): slackX = 0, slackY > 0
 * - If aspects match exactly: both slacks are 0
 *
 * At scale > 1.0:
 * - Both slackX and slackY increase proportionally
 *
 * @param imageWidth - Natural width of the image in pixels
 * @param imageHeight - Natural height of the image in pixels
 * @param viewportWidth - Width of the viewport in pixels
 * @param viewportHeight - Height of the viewport in pixels
 * @param scale - Current content scale (1.0 = aspect-fill minimum)
 * @returns Object with slackX and slackY normalized to image dimensions
 */
export const calculateContentSlack = (
  imageWidth: number,
  imageHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  scale: number = MIN_CONTENT_SCALE
): ContentSlack => {
  // Guard against invalid dimensions
  if (imageWidth <= 0 || imageHeight <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
    return { slackX: 0, slackY: 0 };
  }

  const clampedScale = Math.max(MIN_CONTENT_SCALE, Math.min(MAX_CONTENT_SCALE, scale));

  const viewportAspect = viewportWidth / viewportHeight;
  const imageAspect = imageWidth / imageHeight;

  // Calculate the base source dimensions (aspect-fill at scale 1.0)
  let baseSrcWidth: number;
  let baseSrcHeight: number;

  if (viewportAspect > imageAspect) {
    // Viewport is wider relative to image - use full image width
    baseSrcWidth = imageWidth;
    baseSrcHeight = imageWidth / viewportAspect;
  } else {
    // Viewport is taller relative to image - use full image height
    baseSrcWidth = imageHeight * viewportAspect;
    baseSrcHeight = imageHeight;
  }

  // Apply scale: zooming in means we use a smaller portion of the source image
  const srcWidth = baseSrcWidth / clampedScale;
  const srcHeight = baseSrcHeight / clampedScale;

  // Calculate slack - the available pan range in source image pixels
  const slackX = imageWidth - srcWidth;
  const slackY = imageHeight - srcHeight;

  return { slackX, slackY };
};

/**
 * Checks if panning is possible for the given image/viewport combination.
 * Returns true if there's any slack in either dimension.
 */
export const canPanContent = (
  imageWidth: number,
  imageHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  scale: number = MIN_CONTENT_SCALE
): boolean => {
  const { slackX, slackY } = calculateContentSlack(
    imageWidth,
    imageHeight,
    viewportWidth,
    viewportHeight,
    scale
  );
  // Use small epsilon to handle floating point imprecision
  const epsilon = 0.001;
  return slackX > epsilon || slackY > epsilon;
};

// ============================================================================
// Action Types
// ============================================================================

export interface SplitAction {
  viewportId: string;
  direction: "horizontal" | "vertical";
}

export interface DeleteAction {
  viewportId: string;
}

export interface ZoomAction {
  viewportId: string;
  direction: "in" | "out";
}

interface SplitButtonRect {
  rect: Rect;
  direction: "horizontal" | "vertical";
}

interface ZoomButtonRect {
  rect: Rect;
  direction: "in" | "out";
}

// ============================================================================
// Gallery Background and Content Rect
// ============================================================================

/**
 * Draws the gallery background with the specified color and opacity.
 */
const drawGalleryBackground = (
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  backgroundColor: string,
  opacity: number
): void => {
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  ctx.restore();
};

/**
 * Calculates the content rect for the gallery, accounting for gap options.
 * - gapAroundEdges: adds gap padding on the 3 edges touching the poster border
 * - gapTowardMap: adds gap padding on the edge facing the map area
 *
 * The edge facing the map is determined by gallery position:
 * - Top gallery (y=0): map edge is bottom
 * - Bottom gallery (y>0, full width): map edge is top
 * - Left gallery (x=0, full height): map edge is right
 * - Right gallery (x>0, full height): map edge is left
 *
 * Exported for use in mobile touch event handling.
 */
export const calculateContentRect = (containerRect: Rect, gallery: Gallery): Rect => {
  const gap = gallery.gap;

  // Start with the container rect
  let x = containerRect.x;
  let y = containerRect.y;
  let width = containerRect.width;
  let height = containerRect.height;

  // Determine which edge faces the map based on gallery position
  // Using tolerance for floating point comparison
  const tolerance = 0.001;
  const isAtTop = gallery.position.y < tolerance;
  const isAtBottom = Math.abs(gallery.position.y + gallery.size.height - 1) < tolerance;
  const isAtLeft = gallery.position.x < tolerance;
  const isAtRight = Math.abs(gallery.position.x + gallery.size.width - 1) < tolerance;
  const isFullWidth = Math.abs(gallery.size.width - 1) < tolerance;
  const isFullHeight = Math.abs(gallery.size.height - 1) < tolerance;

  // Determine map-facing edge
  type Edge = "top" | "bottom" | "left" | "right";
  let mapEdge: Edge = "bottom"; // default

  if (isFullWidth && isAtTop && !isAtBottom) {
    mapEdge = "bottom";
  } else if (isFullWidth && isAtBottom && !isAtTop) {
    mapEdge = "top";
  } else if (isFullHeight && isAtLeft && !isAtRight) {
    mapEdge = "right";
  } else if (isFullHeight && isAtRight && !isAtLeft) {
    mapEdge = "left";
  }

  // Apply gap around edges (3 edges touching poster border)
  if (gallery.gapAroundEdges) {
    if (mapEdge !== "top") {
      y += gap;
      height -= gap;
    }
    if (mapEdge !== "bottom") {
      height -= gap;
    }
    if (mapEdge !== "left") {
      x += gap;
      width -= gap;
    }
    if (mapEdge !== "right") {
      width -= gap;
    }
  }

  // Apply gap toward map (the edge facing the map area)
  if (gallery.gapTowardMap) {
    switch (mapEdge) {
      case "top":
        y += gap;
        height -= gap;
        break;
      case "bottom":
        height -= gap;
        break;
      case "left":
        x += gap;
        width -= gap;
        break;
      case "right":
        width -= gap;
        break;
    }
  }

  return { x, y, width, height };
};

/**
 * Calculates the effective gallery dimensions including gap options.
 * Used by external code (e.g., GPS fit bounds calculations) to determine
 * how much space the gallery actually occupies.
 */
export const calculateGalleryEffectiveSize = (
  gallery: Gallery,
  canvasWidth: number,
  canvasHeight: number
): { width: number; height: number } => {
  const containerRect: Rect = {
    x: gallery.position.x * canvasWidth,
    y: gallery.position.y * canvasHeight,
    width: gallery.size.width * canvasWidth,
    height: gallery.size.height * canvasHeight,
  };

  // If gapAroundEdges is enabled, the visual gallery area is larger
  // because it includes the gap padding
  let effectiveWidth = containerRect.width;
  let effectiveHeight = containerRect.height;

  if (gallery.gapAroundEdges) {
    // The gallery effectively takes up more visual space with edge gaps
    // This is already included in containerRect - it's the full gallery area
  }

  return { width: effectiveWidth, height: effectiveHeight };
};

// ============================================================================
// Main Public Function
// ============================================================================

export const drawGallery = ({
  canvasElement,
  project,
  pointerLocation,
  selectedElement,
  isTemplateEditing,
  imageCache,
  selectedViewportId,
}: MapDrawingConfig): {
  elementUnderPointerLocation: MapElement | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horizontal: SnapLine | undefined;
  };
} => {
  const gallery = project.gallery;

  // Early return if no gallery defined or gallery is hidden
  if (!gallery || !gallery.isVisible) {
    return {
      elementUnderPointerLocation: undefined,
      snapLines: { vertical: undefined, horizontal: undefined },
    };
  }

  const ctx = canvasElement.current.getContext("2d");
  if (!ctx) {
    return {
      elementUnderPointerLocation: undefined,
      snapLines: { vertical: undefined, horizontal: undefined },
    };
  }

  // Calculate container rect from ratio-based position/size
  const containerRect: Rect = {
    x: gallery.position.x * ctx.canvas.width,
    y: gallery.position.y * ctx.canvas.height,
    width: gallery.size.width * ctx.canvas.width,
    height: gallery.size.height * ctx.canvas.height,
  };

  // Draw the background (if opacity > 0)
  if (gallery.backgroundOpacity > 0) {
    drawGalleryBackground(ctx, containerRect, gallery.backgroundColor, gallery.backgroundOpacity);
  }

  // Calculate content rect with gap options applied
  const contentRect = calculateContentRect(containerRect, gallery);

  // Determine which viewport (if any) is being hovered for the overlay
  const hoveredViewportResult = pointerLocation
    ? findViewportUnderPointer(
        gallery.content,
        contentRect,
        gallery.gap,
        pointerLocation
      )
    : undefined;
  const hoveredViewportId = hoveredViewportResult?.viewportId;

  // Draw all cells recursively
  gallery.content.forEach((cell) => {
    drawCell(ctx, cell, contentRect, gallery.gap, imageCache, isTemplateEditing, hoveredViewportId, selectedViewportId);
  });

  // Check if gallery is selected
  const isSelected = selectedElement?.gallery?.id === gallery.id;

  // Handle selection UI (resize handles, dotted outline)
  let dragHandleUnderCursor: DragHandle | undefined;

  if (isSelected && isTemplateEditing) {
    const paddedRect = rectWithPadding(containerRect, 5);
    drawSelectedRect(paddedRect, ctx);

    const handleSize = 20;
    const handleWidth = 10;

    // Leading handle (top-left)
    const leadingRect: Rect = {
      x: containerRect.x - handleSize / 2 + handleWidth,
      y: containerRect.y - handleSize / 2 + handleWidth,
      width: handleSize,
      height: handleSize,
    };
    drawResizeHandle(
      ctx,
      { x: leadingRect.x, y: leadingRect.y },
      handleSize,
      handleWidth,
      "top-left"
    );

    // Trailing handle (bottom-right)
    const trailingRect: Rect = {
      x: containerRect.x + containerRect.width - handleSize / 2,
      y: containerRect.y + containerRect.height - handleSize / 2,
      width: handleSize,
      height: handleSize,
    };
    drawResizeHandle(
      ctx,
      {
        x: trailingRect.x + handleSize - handleWidth / 2,
        y: trailingRect.y + handleSize - handleWidth / 2,
      },
      handleSize,
      handleWidth,
      "bottom-right"
    );

    // Check if pointer is over resize handles
    if (pointerLocation) {
      if (pointIsInsideRect(pointerLocation, leadingRect)) {
        dragHandleUnderCursor = { position: "leading" };
      } else if (pointIsInsideRect(pointerLocation, trailingRect)) {
        dragHandleUnderCursor = { position: "trailing" };
      }
    }
  }

  // Hit detection - use contentRect for viewport detection (matches drawing coordinates)
  const hitResult = findElementUnderPointer(
    gallery,
    containerRect,
    contentRect,
    pointerLocation,
    isSelected,
    dragHandleUnderCursor,
    isTemplateEditing,
    selectedViewportId,
    imageCache
  );

  return {
    elementUnderPointerLocation: hitResult,
    snapLines: { vertical: undefined, horizontal: undefined },
  };
};

// ============================================================================
// Recursive Drawing Functions
// ============================================================================

/**
 * Draws a cell and its items (viewports or nested cells) recursively.
 */
const drawCell = (
  ctx: CanvasRenderingContext2D,
  cell: GalleryCell,
  parentRect: Rect,
  gap: number,
  imageCache: Map<string, HTMLImageElement> | undefined,
  isTemplateEditing: boolean,
  hoveredViewportId: string | undefined,
  selectedViewportId: string | undefined
): void => {
  // Calculate cell's absolute rect from its ratio-based position/size
  const cellRect = calculateRect(cell.position, cell.size, parentRect);

  const itemCount = cell.items.length;
  if (itemCount === 0) return;

  // Calculate item sizes accounting for gaps
  const totalGap = (itemCount - 1) * gap;
  const isHorizontal = cell.orientation === "LANDSCAPE";

  let currentOffset = 0;

  cell.items.forEach((item, index) => {
    const itemRect = calculateItemRect(
      item,
      cellRect,
      cell.orientation,
      gap,
      itemCount,
      index,
      currentOffset,
      totalGap
    );

    if (item.type === "viewport") {
      const isHovered = hoveredViewportId === item.viewport.id;
      const isSelected = selectedViewportId === item.viewport.id;
      drawViewport(ctx, item.viewport, itemRect, imageCache, isTemplateEditing, isHovered, isSelected);
    } else {
      // Recurse into nested cell - pass itemRect as the parent rect for the nested cell
      drawCell(ctx, item.cell, itemRect, gap, imageCache, isTemplateEditing, hoveredViewportId, selectedViewportId);
    }

    // Update offset for next item
    if (isHorizontal) {
      currentOffset = itemRect.x + itemRect.width + gap - cellRect.x;
    } else {
      currentOffset = itemRect.y + itemRect.height + gap - cellRect.y;
    }
  });
};

/**
 * Calculate the rect for an item based on its position within parent cell.
 * Items within a cell are always equally distributed based on the cell's orientation.
 * The item's own position/size properties are not used for layout within the parent cell.
 */
const calculateItemRect = (
  _item: GalleryCellItem,
  parentRect: Rect,
  orientation: "LANDSCAPE" | "PORTRAIT",
  gap: number,
  itemCount: number,
  _index: number,
  currentOffset: number,
  totalGap: number
): Rect => {
  // Calculate based on equal distribution with gaps
  const isHorizontal = orientation === "LANDSCAPE";
  const availableSize = isHorizontal
    ? parentRect.width - totalGap
    : parentRect.height - totalGap;
  const itemSize = availableSize / itemCount;

  if (isHorizontal) {
    return {
      x: parentRect.x + currentOffset,
      y: parentRect.y,
      width: itemSize,
      height: parentRect.height,
    };
  } else {
    return {
      x: parentRect.x,
      y: parentRect.y + currentOffset,
      width: parentRect.width,
      height: itemSize,
    };
  }
};

/**
 * Draws a single viewport (leaf node in the gallery tree).
 */
const drawViewport = (
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  rect: Rect,
  imageCache: Map<string, HTMLImageElement> | undefined,
  isTemplateEditing: boolean,
  isHovered: boolean,
  isSelected: boolean
): void => {
  const imageElement = imageCache?.get(`gallery-${viewport.imageID}`);
  const isImageLoaded =
    imageElement?.complete && imageElement?.naturalHeight !== 0;

  if (isImageLoaded && viewport.imageID) {
    // Draw image with aspect-fill, applying pan/zoom from viewport content state
    drawAspectFillImage(
      ctx,
      imageElement,
      rect,
      viewport.contentOffset,
      viewport.contentScale
    );

    // Note: Change image overlay is no longer drawn here - it's now in the HTML toolbar
  } else {
    // Draw placeholder gray background
    ctx.fillStyle = "#E0E0E0";
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

    // Draw + icon for empty viewports
    drawPlusIcon(ctx, rect);
  }

  // Note: Split controls and delete button are no longer drawn here - they're now in the HTML toolbar

  // Draw selection indicator when viewport is selected
  if (isSelected) {
    drawViewportSelectionIndicator(ctx, rect);
    // Note: Zoom controls are no longer drawn here - they're now in the HTML toolbar
  }
};

/**
 * Draws a selection indicator (blue border) around a selected viewport.
 */
const drawViewportSelectionIndicator = (
  ctx: CanvasRenderingContext2D,
  rect: Rect
): void => {
  ctx.save();

  const borderWidth = 4;
  const borderColor = "#ff0066"; // Interactive color (matches --color-interactive)

  ctx.strokeStyle = borderColor;
  ctx.lineWidth = borderWidth;

  // Draw inset border so it doesn't extend outside the viewport
  const inset = borderWidth / 2;
  ctx.strokeRect(
    rect.x + inset,
    rect.y + inset,
    rect.width - borderWidth,
    rect.height - borderWidth
  );

  ctx.restore();
};

/**
 * Draws zoom controls (+/- buttons) at the bottom-center of a selected viewport.
 */
const drawZoomControls = (
  ctx: CanvasRenderingContext2D,
  rect: Rect
): void => {
  const buttonSize = Math.min(32, rect.width * 0.12, rect.height * 0.12);
  const buttonGap = 8;
  const bottomPadding = 12;

  // Calculate positions for both buttons centered at bottom
  const totalWidth = buttonSize * 2 + buttonGap;
  const startX = rect.x + (rect.width - totalWidth) / 2;
  const buttonY = rect.y + rect.height - buttonSize - bottomPadding;

  // Draw zoom out button (-)
  drawZoomButton(ctx, startX, buttonY, buttonSize, "out");

  // Draw zoom in button (+)
  drawZoomButton(ctx, startX + buttonSize + buttonGap, buttonY, buttonSize, "in");
};

/**
 * Draws a single zoom button with the appropriate icon (+ or -).
 */
const drawZoomButton = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  direction: "in" | "out"
): void => {
  ctx.save();

  // Draw circular button background
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
  ctx.fill();

  // Draw icon
  const iconSize = size * 0.4;
  const centerX = x + size / 2;
  const centerY = y + size / 2;

  ctx.strokeStyle = "white";
  ctx.lineWidth = 2.5;
  ctx.lineCap = "round";

  ctx.beginPath();
  // Horizontal line (for both + and -)
  ctx.moveTo(centerX - iconSize / 2, centerY);
  ctx.lineTo(centerX + iconSize / 2, centerY);

  // Vertical line (only for +)
  if (direction === "in") {
    ctx.moveTo(centerX, centerY - iconSize / 2);
    ctx.lineTo(centerX, centerY + iconSize / 2);
  }
  ctx.stroke();

  ctx.restore();
};

/**
 * Calculates the rects for zoom buttons on a viewport.
 * Used for hit detection.
 */
const getZoomButtonRects = (viewportRect: Rect): ZoomButtonRect[] => {
  const buttonSize = Math.min(32, viewportRect.width * 0.12, viewportRect.height * 0.12);
  const buttonGap = 8;
  const bottomPadding = 12;

  const totalWidth = buttonSize * 2 + buttonGap;
  const startX = viewportRect.x + (viewportRect.width - totalWidth) / 2;
  const buttonY = viewportRect.y + viewportRect.height - buttonSize - bottomPadding;

  return [
    {
      rect: { x: startX, y: buttonY, width: buttonSize, height: buttonSize },
      direction: "out" as const,
    },
    {
      rect: { x: startX + buttonSize + buttonGap, y: buttonY, width: buttonSize, height: buttonSize },
      direction: "in" as const,
    },
  ];
};

/**
 * Check if pointer is over a zoom button on a selected viewport.
 */
const findZoomButtonUnderPointer = (
  viewportRect: Rect,
  pointerLocation: Point
): { direction: "in" | "out" } | undefined => {
  const zoomButtons = getZoomButtonRects(viewportRect);

  for (const button of zoomButtons) {
    // Use circular hit detection for the buttons
    const centerX = button.rect.x + button.rect.width / 2;
    const centerY = button.rect.y + button.rect.height / 2;
    const radius = button.rect.width / 2;

    const dx = pointerLocation.x - centerX;
    const dy = pointerLocation.y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= radius) {
      return { direction: button.direction };
    }
  }

  return undefined;
};

/**
 * Draws split control buttons (horizontal and vertical) on a viewport.
 * Positioned in the bottom-left corner to avoid conflicting with the change image button (top-right).
 */
const drawSplitControls = (
  ctx: CanvasRenderingContext2D,
  rect: Rect
): void => {
  const buttonSize = Math.min(28, rect.width * 0.15, rect.height * 0.15);
  const buttonPadding = 8;
  const buttonGap = 4;

  // Position buttons in bottom-left corner
  const horizontalButtonX = rect.x + buttonPadding;
  const horizontalButtonY = rect.y + rect.height - buttonSize - buttonPadding;

  const verticalButtonX = horizontalButtonX + buttonSize + buttonGap;
  const verticalButtonY = horizontalButtonY;

  // Draw horizontal split button (─)
  drawSplitButton(ctx, horizontalButtonX, horizontalButtonY, buttonSize, "horizontal");

  // Draw vertical split button (│)
  drawSplitButton(ctx, verticalButtonX, verticalButtonY, buttonSize, "vertical");
};

/**
 * Draws a single split button with the appropriate icon.
 */
const drawSplitButton = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  direction: "horizontal" | "vertical"
): void => {
  ctx.save();

  // Draw circular button background
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
  ctx.fill();

  // Draw split icon
  const iconSize = size * 0.5;
  const centerX = x + size / 2;
  const centerY = y + size / 2;

  ctx.strokeStyle = "white";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";

  ctx.beginPath();
  if (direction === "horizontal") {
    // Draw horizontal split icon: two rectangles side by side with a vertical line between
    // Left half indicator
    ctx.moveTo(centerX - iconSize / 2, centerY - iconSize / 3);
    ctx.lineTo(centerX - iconSize / 2, centerY + iconSize / 3);
    // Right half indicator
    ctx.moveTo(centerX + iconSize / 2, centerY - iconSize / 3);
    ctx.lineTo(centerX + iconSize / 2, centerY + iconSize / 3);
    // Center divider (vertical line)
    ctx.moveTo(centerX, centerY - iconSize / 2);
    ctx.lineTo(centerX, centerY + iconSize / 2);
  } else {
    // Draw vertical split icon: two rectangles stacked with a horizontal line between
    // Top half indicator
    ctx.moveTo(centerX - iconSize / 3, centerY - iconSize / 2);
    ctx.lineTo(centerX + iconSize / 3, centerY - iconSize / 2);
    // Bottom half indicator
    ctx.moveTo(centerX - iconSize / 3, centerY + iconSize / 2);
    ctx.lineTo(centerX + iconSize / 3, centerY + iconSize / 2);
    // Center divider (horizontal line)
    ctx.moveTo(centerX - iconSize / 2, centerY);
    ctx.lineTo(centerX + iconSize / 2, centerY);
  }
  ctx.stroke();

  ctx.restore();
};

/**
 * Calculates the rects for split buttons on a viewport.
 * Used for hit detection.
 */
const getSplitButtonRects = (viewportRect: Rect): SplitButtonRect[] => {
  const buttonSize = Math.min(28, viewportRect.width * 0.15, viewportRect.height * 0.15);
  const buttonPadding = 8;
  const buttonGap = 4;

  const horizontalButtonX = viewportRect.x + buttonPadding;
  const horizontalButtonY = viewportRect.y + viewportRect.height - buttonSize - buttonPadding;

  const verticalButtonX = horizontalButtonX + buttonSize + buttonGap;
  const verticalButtonY = horizontalButtonY;

  return [
    {
      rect: { x: horizontalButtonX, y: horizontalButtonY, width: buttonSize, height: buttonSize },
      direction: "horizontal" as const,
    },
    {
      rect: { x: verticalButtonX, y: verticalButtonY, width: buttonSize, height: buttonSize },
      direction: "vertical" as const,
    },
  ];
};

/**
 * Draws the delete button on a viewport.
 * Positioned in the bottom-right corner.
 */
const drawDeleteButton = (
  ctx: CanvasRenderingContext2D,
  rect: Rect
): void => {
  const buttonSize = Math.min(28, rect.width * 0.15, rect.height * 0.15);
  const buttonPadding = 8;

  // Position in bottom-right corner
  const buttonX = rect.x + rect.width - buttonSize - buttonPadding;
  const buttonY = rect.y + rect.height - buttonSize - buttonPadding;

  ctx.save();

  // Draw circular button background
  ctx.beginPath();
  ctx.arc(buttonX + buttonSize / 2, buttonY + buttonSize / 2, buttonSize / 2, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(180, 50, 50, 0.8)";
  ctx.fill();

  // Draw X icon
  const iconSize = buttonSize * 0.35;
  const centerX = buttonX + buttonSize / 2;
  const centerY = buttonY + buttonSize / 2;

  ctx.strokeStyle = "white";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";

  ctx.beginPath();
  // Draw X shape
  ctx.moveTo(centerX - iconSize, centerY - iconSize);
  ctx.lineTo(centerX + iconSize, centerY + iconSize);
  ctx.moveTo(centerX + iconSize, centerY - iconSize);
  ctx.lineTo(centerX - iconSize, centerY + iconSize);
  ctx.stroke();

  ctx.restore();
};

/**
 * Gets the rect for the delete button on a viewport.
 * Used for hit detection.
 */
const getDeleteButtonRect = (viewportRect: Rect): Rect => {
  const buttonSize = Math.min(28, viewportRect.width * 0.15, viewportRect.height * 0.15);
  const buttonPadding = 8;

  return {
    x: viewportRect.x + viewportRect.width - buttonSize - buttonPadding,
    y: viewportRect.y + viewportRect.height - buttonSize - buttonPadding,
    width: buttonSize,
    height: buttonSize,
  };
};

/**
 * Gets the rect for the change image button on a populated viewport.
 * Used for hit detection. Button is positioned in top-right corner.
 */
const getChangeImageButtonRect = (viewportRect: Rect): Rect => {
  const buttonSize = Math.min(36, viewportRect.width * 0.25, viewportRect.height * 0.25);
  const buttonPadding = 8;

  return {
    x: viewportRect.x + viewportRect.width - buttonSize - buttonPadding,
    y: viewportRect.y + buttonPadding,
    width: buttonSize,
    height: buttonSize,
  };
};

/**
 * Draws an image with aspect-fill (crop to fill entire rect).
 * Supports pan (contentOffset) and zoom (contentScale) for viewport content editing.
 *
 * Note: contentOffset and contentScale should be pre-validated by updateViewportContent().
 * This function assumes valid input but applies defensive clamping as a fallback.
 *
 * @param contentOffset - Pan offset in normalized range [MIN_CONTENT_OFFSET, MAX_CONTENT_OFFSET] where 0 = centered
 * @param contentScale - Zoom level in range [MIN_CONTENT_SCALE, MAX_CONTENT_SCALE] where 1.0 = aspect-fill minimum
 */
const drawAspectFillImage = (
  ctx: CanvasRenderingContext2D,
  imageElement: HTMLImageElement,
  rect: Rect,
  contentOffset: Point = { x: 0, y: 0 },
  contentScale: number = MIN_CONTENT_SCALE
): void => {
  // Guard against zero or negative dimensions that would produce NaN/Infinity
  if (
    imageElement.width <= 0 ||
    imageElement.height <= 0 ||
    rect.width <= 0 ||
    rect.height <= 0
  ) {
    return;
  }

  // Clamp scale to valid range (defensive - should be pre-validated)
  const scale = Math.max(MIN_CONTENT_SCALE, Math.min(MAX_CONTENT_SCALE, contentScale));

  ctx.save();
  ctx.beginPath();
  ctx.rect(rect.x, rect.y, rect.width, rect.height);
  ctx.clip();

  const canvasAspect = rect.width / rect.height;
  const imageAspect = imageElement.width / imageElement.height;

  // Calculate the base source dimensions (aspect-fill at scale 1.0)
  let baseSrcWidth: number;
  let baseSrcHeight: number;

  if (canvasAspect > imageAspect) {
    // Canvas is wider relative to image - use full image width
    baseSrcWidth = imageElement.width;
    baseSrcHeight = imageElement.width / canvasAspect;
  } else {
    // Canvas is taller relative to image - use full image height
    baseSrcWidth = imageElement.height * canvasAspect;
    baseSrcHeight = imageElement.height;
  }

  // Apply scale: zooming in means we use a smaller portion of the source image
  const srcWidth = baseSrcWidth / scale;
  const srcHeight = baseSrcHeight / scale;

  // Calculate "slack" - the available pan range in source image pixels
  // Slack is how much extra image we have beyond what's visible
  // At scale 1.0, slack is 0 (no panning possible)
  // At scale > 1.0, slack increases allowing pan within bounds
  const slackX = imageElement.width - srcWidth;
  const slackY = imageElement.height - srcHeight;

  // Calculate source position using pre-validated offset
  // Formula: srcPos = slack * (0.5 + offset)
  // At offset 0: centered (srcPos = slack/2)
  // At offset -0.5: left/top edge (srcPos = 0)
  // At offset +0.5: right/bottom edge (srcPos = slack)
  const srcX = slackX * (0.5 + contentOffset.x);
  const srcY = slackY * (0.5 + contentOffset.y);

  ctx.drawImage(
    imageElement,
    srcX,
    srcY,
    srcWidth,
    srcHeight,
    rect.x,
    rect.y,
    rect.width,
    rect.height
  );
  ctx.restore();
};

/**
 * Draws a + icon centered in the rect.
 */
const drawPlusIcon = (ctx: CanvasRenderingContext2D, rect: Rect): void => {
  const iconSize = Math.min(24, rect.width * 0.3, rect.height * 0.3);
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;

  ctx.save();
  ctx.strokeStyle = "white";
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
  ctx.shadowBlur = 4;

  // Draw + shape
  ctx.beginPath();
  ctx.moveTo(centerX - iconSize / 2, centerY);
  ctx.lineTo(centerX + iconSize / 2, centerY);
  ctx.moveTo(centerX, centerY - iconSize / 2);
  ctx.lineTo(centerX, centerY + iconSize / 2);
  ctx.stroke();

  ctx.restore();
};

/**
 * Draws a change image overlay with a + button on populated viewports when hovered.
 */
const drawChangeImageOverlay = (
  ctx: CanvasRenderingContext2D,
  rect: Rect
): void => {
  const buttonSize = Math.min(36, rect.width * 0.25, rect.height * 0.25);
  const buttonPadding = 8;
  const buttonX = rect.x + rect.width - buttonSize - buttonPadding;
  const buttonY = rect.y + buttonPadding;

  ctx.save();

  // Draw circular button background
  ctx.beginPath();
  ctx.arc(
    buttonX + buttonSize / 2,
    buttonY + buttonSize / 2,
    buttonSize / 2,
    0,
    Math.PI * 2
  );
  ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
  ctx.fill();

  // Draw + icon inside the button
  const iconSize = buttonSize * 0.5;
  const centerX = buttonX + buttonSize / 2;
  const centerY = buttonY + buttonSize / 2;

  ctx.strokeStyle = "white";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";

  ctx.beginPath();
  ctx.moveTo(centerX - iconSize / 2, centerY);
  ctx.lineTo(centerX + iconSize / 2, centerY);
  ctx.moveTo(centerX, centerY - iconSize / 2);
  ctx.lineTo(centerX, centerY + iconSize / 2);
  ctx.stroke();

  ctx.restore();
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate absolute rect from ratio-based position/size relative to parent.
 */
const calculateRect = (
  position: Point,
  size: { width: number; height: number },
  parentRect: Rect
): Rect => {
  return {
    x: parentRect.x + position.x * parentRect.width,
    y: parentRect.y + position.y * parentRect.height,
    width: size.width * parentRect.width,
    height: size.height * parentRect.height,
  };
};

// ============================================================================
// Hit Detection
// ============================================================================

/**
 * Find which element (gallery container or viewport) is under the pointer.
 * @param containerRect - The full gallery container rect (for drag handles and general hit detection)
 * @param contentRect - The rect where cells are drawn (accounts for gap options)
 */
const findElementUnderPointer = (
  gallery: Gallery,
  containerRect: Rect,
  contentRect: Rect,
  pointerLocation: Point | undefined,
  _isSelected: boolean,
  dragHandleUnderCursor: DragHandle | undefined,
  isTemplateEditing: boolean,
  selectedViewportId: string | undefined,
  imageCache: Map<string, HTMLImageElement> | undefined
): MapElement | undefined => {
  if (!pointerLocation) {
    return undefined;
  }

  // If we found a drag handle, return that
  if (dragHandleUnderCursor) {
    return {
      type: "gallery",
      rect: containerRect,
      element: gallery,
      dragHandleUnderCursor,
    };
  }

  // Check if pointer is inside gallery container
  if (!pointIsInsideRect(pointerLocation, containerRect)) {
    return undefined;
  }

  // Check for viewport under pointer - use contentRect which matches where cells are drawn
  const viewportResult = findViewportUnderPointer(
    gallery.content,
    contentRect,
    gallery.gap,
    pointerLocation
  );

  if (viewportResult) {
    // Check if image actually loaded (not just that imageID exists)
    // This ensures failed images are treated as empty so users can upload a replacement
    const imageElement = imageCache?.get(`gallery-${viewportResult.viewport.imageID}`);
    const isImageLoaded = imageElement?.complete && imageElement?.naturalHeight !== 0;
    const hasImage = !!viewportResult.viewport.imageID && isImageLoaded;

    // Note: Zoom, split, delete, and change image buttons are now handled by the HTML ViewportToolbar.
    // The only canvas-level interaction is:
    // 1. Clicking on empty viewport triggers image upload
    // 2. Clicking on viewport returns the viewport for selection

    // For empty viewports: clicking anywhere triggers image upload
    if (!hasImage) {
      return {
        type: "gallery",
        rect: viewportResult.rect,
        element: gallery,
        viewportId: viewportResult.viewportId,
        changeImageAction: {
          viewportId: viewportResult.viewportId,
        },
      };
    }

    // Clicking on a populated viewport - return viewport for selection
    return {
      type: "gallery",
      rect: viewportResult.rect,
      element: gallery,
      viewportId: viewportResult.viewportId,
    };
  }

  // Pointer is inside gallery but not on a specific viewport
  return {
    type: "gallery",
    rect: containerRect,
    element: gallery,
  };
};

/**
 * Check if pointer is over a split button on a viewport.
 */
const findSplitButtonUnderPointer = (
  viewportRect: Rect,
  pointerLocation: Point
): { direction: "horizontal" | "vertical" } | undefined => {
  const splitButtons = getSplitButtonRects(viewportRect);

  for (const button of splitButtons) {
    // Use circular hit detection for the buttons
    const centerX = button.rect.x + button.rect.width / 2;
    const centerY = button.rect.y + button.rect.height / 2;
    const radius = button.rect.width / 2;

    const dx = pointerLocation.x - centerX;
    const dy = pointerLocation.y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= radius) {
      return { direction: button.direction };
    }
  }

  return undefined;
};

/**
 * Check if pointer is over the delete button on a viewport.
 */
const findDeleteButtonUnderPointer = (
  viewportRect: Rect,
  pointerLocation: Point
): boolean => {
  const buttonRect = getDeleteButtonRect(viewportRect);

  // Use circular hit detection for the button
  const centerX = buttonRect.x + buttonRect.width / 2;
  const centerY = buttonRect.y + buttonRect.height / 2;
  const radius = buttonRect.width / 2;

  const dx = pointerLocation.x - centerX;
  const dy = pointerLocation.y - centerY;
  const distance = Math.sqrt(dx * dx + dy * dy);

  return distance <= radius;
};

/**
 * Check if pointer is over the change image button on a viewport.
 */
const findChangeImageButtonUnderPointer = (
  viewportRect: Rect,
  pointerLocation: Point
): boolean => {
  const buttonRect = getChangeImageButtonRect(viewportRect);

  // Use circular hit detection for the button
  const centerX = buttonRect.x + buttonRect.width / 2;
  const centerY = buttonRect.y + buttonRect.height / 2;
  const radius = buttonRect.width / 2;

  const dx = pointerLocation.x - centerX;
  const dy = pointerLocation.y - centerY;
  const distance = Math.sqrt(dx * dx + dy * dy);

  return distance <= radius;
};

/**
 * Recursively find viewport under pointer within cells.
 * Exported for use in mobile touch event handling.
 */
export const findViewportUnderPointer = (
  cells: GalleryCell[],
  parentRect: Rect,
  gap: number,
  pointerLocation: Point
): { viewportId: string; rect: Rect; viewport: Viewport } | undefined => {
  for (const cell of cells) {
    const cellRect = calculateRect(cell.position, cell.size, parentRect);

    const itemCount = cell.items.length;
    if (itemCount === 0) continue;

    const totalGap = (itemCount - 1) * gap;
    let currentOffset = 0;

    for (let i = 0; i < cell.items.length; i++) {
      const item = cell.items[i];
      const itemRect = calculateItemRect(
        item,
        cellRect,
        cell.orientation,
        gap,
        itemCount,
        i,
        currentOffset,
        totalGap
      );

      if (item.type === "viewport") {
        if (pointIsInsideRect(pointerLocation, itemRect)) {
          return { viewportId: item.viewport.id, rect: itemRect, viewport: item.viewport };
        }
      } else {
        // Recurse into nested cell - pass itemRect as the parent rect for the nested cell
        const result = findViewportUnderPointer(
          [item.cell],
          itemRect,
          gap,
          pointerLocation
        );
        if (result) return result;
      }

      // Update offset for next item
      const isHorizontal = cell.orientation === "LANDSCAPE";
      if (isHorizontal) {
        currentOffset = itemRect.x + itemRect.width + gap - cellRect.x;
      } else {
        currentOffset = itemRect.y + itemRect.height + gap - cellRect.y;
      }
    }
  }

  return undefined;
};
